import React, { FC, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Box, Button, Card, Stack, Typography } from '@mui/material';

import { ProcessTestSummaryDTO } from '@/api/generated';
import PlusIcon from '@/assets/icons/plus.svg?react';
import { PROCESS_SETTINGS_STEP_KEYS, toProcessSettingsForm } from '@/root/linker';
import { borders } from '@/theme/constants';
import typography from '@/theme/typography';

interface TestListProps {
  tests?: ProcessTestSummaryDTO[];
  processId: string;
}

const TestList: FC<TestListProps> = ({ tests, processId }) => {
  const { t } = useTranslation('processes');
  const navigate = useNavigate();
  const sortedTests = useMemo(() => {
    if (!tests) return [];

    return [...tests].sort((a, b) => (a.order || 0) - (b.order || 0));
  }, [tests]);

  const handleManageTests = useCallback(() => {
    navigate(
      toProcessSettingsForm({
        processId,
        tabKey: PROCESS_SETTINGS_STEP_KEYS.TESTS
      })
    );
  }, [navigate, processId]);

  if (!sortedTests.length) {
    return null;
  }

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="body2" sx={{ fontWeight: typography.fontWeightMedium, color: 'grey.700' }}>
          {t('layouts.processes.details.info.testList.title')}
        </Typography>
        <Button variant="text" size="small" color="primary" startIcon={<PlusIcon />} onClick={handleManageTests}>
          {t('layouts.processes.details.info.testList.manageTestsButton')}
        </Button>
      </Stack>
      <Stack gap={1}>
        {sortedTests.map((test, index) => (
          <Card
            key={test.catalogItemId}
            elevation={0}
            sx={{
              bgcolor: 'background.default',
              borderRadius: 1,
              border: borders.half,
              borderColor: 'grey.400',
              p: 0
            }}
          >
            <Stack direction="row" alignItems="center">
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="center"
                sx={{
                  bgcolor: 'grey.200',
                  height: 40,
                  width: 30
                }}
              >
                <Typography
                  sx={{
                    fontSize: 24,
                    color: 'primary.main',
                    fontWeight: typography.fontWeightMedium,
                    minWidth: 24,
                    textAlign: 'center'
                  }}
                >
                  {test.order || index + 1}
                </Typography>
              </Stack>
              <Stack direction="row" alignItems="center" gap={1} justifyContent="start" px={2}>
                <Typography variant="body2" sx={{ flexGrow: 1 }}>
                  {test.name}
                </Typography>
              </Stack>
            </Stack>
          </Card>
        ))}
      </Stack>
    </Box>
  );
};

export default TestList;
